import { z } from "zod";
import type { InvestingCharacteristics } from "@/components/widgets/StreamChat/ui/components/UserChatProfile/types";
import type { FormSchema, InvestingCharacteristicRatingTooltip } from "./types";

export const investingCharacteristicLabels: Record<
	Exclude<keyof InvestingCharacteristics, "investingStyle">,
	string
> = {
	options: "Options",
	riskTolerance: "Risk Tolerance",
	investmentHorizon: "Investment Horizon",
	equities: "Equities",
	etfFunds: "ETF/Funds",
	futures: "Futures",
	fixedIncome: "Fixed Income",
	expertise: "Expertise",
	mutualFunds: "Mutual Funds",
	portfolioSize: "Portfolio Size",
	investmentDuration: "Investment Duration",
};

export const investingStyleLabels: Record<
	keyof FormSchema["investingCharacteristics"]["investingStyle"],
	string
> = {
	growth: "Growth",
	income: "Income",
	value: "Value",
	all: "All",
};

export const onlineAddressLabels: Record<
	keyof FormSchema["onlineAddresses"],
	string
> = {
	website: "Website",
	twitter: "X.com (Twitter)",
};

export const formSchema = z.object({
	imageId: z.number(),
	firstName: z.string(),
	lastName: z.string(),
	email: z.string(),
	phone: z.string().optional(),
	userName: z
		.string()
		.trim()
		.min(1, "Required")
		.max(20, "String must contain at most 20 characters"),
	aboutMe: z
		.string()
		.max(1000, "String must contain at most 1000 characters")
		.trim()
		.optional(),
	goal: z
		.string()
		.max(1000, "String must contain at most 1000 characters")
		.trim()
		.optional(),
	publicProfile: z.boolean().optional(),
	investingCharacteristics: z.object({
		riskTolerance: z.number().optional(),
		investmentHorizon: z.number().optional(),
		equities: z.number().optional(),
		etfFunds: z.number().optional(),
		options: z.number().optional(),
		futures: z.number().optional(),
		fixedIncome: z.number().optional(),
		investingStyle: z.object({
			growth: z.boolean().optional(),
			income: z.boolean().optional(),
			value: z.boolean().optional(),
			all: z.boolean().optional(),
		}),
	}),
	showOnlineAddresses: z.boolean().optional(),
	onlineAddresses: z.object({
		website: z
			.string()
			.max(50, "String must contain at most 50 characters")
			.trim()
			.refine(
				(value) =>
					!value ||
					/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})(\/[\w.-]*)*\/?$/.test(
						value,
					),
				{
					message: "Invalid URL",
				},
			)
			.optional(),
		twitter: z
			.string()
			.max(50, "String must contain at most 50 characters")
			.trim()
			.optional(),
	}),
});

export const investingCharacteristicRatingTooltips: Record<
	InvestingCharacteristicRatingTooltip,
	string[]
> = {
	riskTolerance: [
		"Risk Averse",
		"Medium Risk",
		"Very High Risk",
		"I add dopamine in my coffee",
	],
	investmentHorizon: [
		"Trader (a few hours/days)",
		"Swing Trader (days/weeks)",
		"Investor (quarter/years)",
		"More than W. Buffett",
	],
	default: ["Beginner", "Some experience", "Expert", "Master"],
};
